import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { aParams, desdeParams, type EsquemaFiltros, type Filtros } from '@/lib/url-filtros';

/**
 * A1 · Los filtros de una pantalla, viviendo en la URL.
 *
 * Reemplaza al `useState` por filtro que había en cada pantalla. Con esto:
 *
 *   - Recargar conserva los filtros.
 *   - Atrás y adelante del navegador funcionan.
 *   - Un enlace con filtros se comparte y el otro ve exactamente lo mismo.
 *
 * Ese último punto es lo que hace posible la Fase A5 (los números del tablero
 * abren un listado ya filtrado) y la B4 (los botones del copiloto). Sin filtros
 * direccionables, un botón que "abre el listado filtrado" no tiene a dónde
 * apuntar.
 *
 * Toda la lógica de serialización es pura y vive en `lib/url-filtros`; este hook
 * es solo el puente con el router.
 */
// `T extends object` y no `extends Filtros`: exigir la firma de indice obligaria
// a cada pantalla a ensuciar su tipo de filtros solo para satisfacer al hook.
export function useFiltrosUrl<T extends object>(esquema: EsquemaFiltros) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Se recalcula solo cuando cambia la URL, no en cada render.
  const filtros = useMemo(
    () => desdeParams(searchParams, esquema) as T,
    // `esquema` es una constante de módulo en cada pantalla; incluirlo en las
    // dependencias obligaría a memoizarlo en siete lugares.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams],
  );

  /** Cambia uno o varios filtros. Los demás se conservan. */
  const setFiltros = useCallback(
    (cambios: Partial<T>) => {
      const siguiente = { ...filtros, ...cambios } as Filtros;
      // `replace` a propósito: escribir una entrada de historial por cada tecla
      // del buscador dejaría el botón "atrás" inservible. Cambiar de pantalla
      // sí crea entrada; cambiar un filtro, no.
      setSearchParams(aParams(siguiente, esquema), { replace: true });
    },
    [filtros, esquema, setSearchParams],
  );

  /** Deja la pantalla en su estado por defecto: URL sin parámetros. */
  const limpiarFiltros = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** ¿Hay algún filtro activo? Para pintar el botón de limpiar. */
  const hayFiltros = useMemo(() => aParams(filtros as Filtros, esquema).toString() !== '', [filtros, esquema]);

  return { filtros, setFiltros, limpiarFiltros, hayFiltros };
}

/**
 * Arma el `?query` de una pantalla desde fuera de ella. PURO.
 *
 * Es lo que usan el tablero (A5) y, más adelante, los botones del copiloto
 * (B4): construyen el enlace sin montar la pantalla ni tocar su estado.
 *
 * Devuelve la ruta completa lista para `navigate()` o para un `<Link to>`.
 */
export function enlaceConFiltros(ruta: string, filtros: Filtros, esquema: EsquemaFiltros): string {
  const qs = aParams(filtros, esquema).toString();
  return qs ? `${ruta}?${qs}` : ruta;
}
